import * as React from 'react';
import { DeepReadonly, LocationWithImageData, OpeningHours } from 'lib/types';
import classnames from 'classnames';
import BondMapMarker from 'assets/bond-map-marker.inline.svg';
import Clock from 'assets/clock.inline.svg';
import { Link } from 'gatsby';
import { FaChevronRight } from 'react-icons/fa';
import useStartBookingFlow from 'booking/hooks/useStartBookingFlow';
import ExternalLink from 'core/components/ExternalLink';
import { mapOpeningHours } from 'locations/lib/util';
import useYelpTracking from 'analytics/hooks/useYelpTracking';
import { useIntl } from 'react-intl';
import CallToAction from 'core/components/CallToAction';
import * as styles from './ClinicCard.module.scss';

export type ClinicCardProps = {
    location: LocationWithImageData;
    isSelected?: boolean;
    showDetailsLink?: boolean;
};

const MAPS_LINK = 'https://www.google.com/maps/search/?api=1&query=';

function ClinicCard({
    location,
    isSelected,
    showDetailsLink,
}: ClinicCardProps) {
    const startBooking = useStartBookingFlow();
    const intl = useIntl();

    const {
        _id: id,
        slug,
        name,
        fullAddress,
        locationHours,
        googleLocation,
    } = location;

    const addressString = fullAddress
        ? // eslint-disable-next-line max-len
          `${fullAddress?.streetAddress}, ${fullAddress?.city}, ${fullAddress?.state} ${fullAddress.zipCode}`
        : null;

    const mapsLink =
        googleLocation?.metadata?.mapsUrl ??
        MAPS_LINK + encodeURIComponent(addressString ?? '');

    const mapLocationHours = (
        openingTimes: DeepReadonly<Partial<OpeningHours>>,
    ) => {
        const mappedHours = mapOpeningHours(
            openingTimes as Partial<OpeningHours>,
        );

        return mappedHours.map(({ days, hours }) => (
            <React.Fragment key={`${days}_${hours}`}>
                <div>{days}:</div>
                <div>{hours}</div>
            </React.Fragment>
        ));
    };

    const { trackGetDirections } = useYelpTracking();

    return (
        <div
            className={classnames(styles.location, {
                [styles.current]: isSelected,
            })}
        >
            <h4>{name}</h4>
            {showDetailsLink && (
                <Link to={`/c/${slug}`} className={styles.detailsLink}>
                    See Details
                    <FaChevronRight aria-hidden="true" />
                </Link>
            )}

            {addressString && (
                <div className={styles.iconRow}>
                    <BondMapMarker
                        aria-label={intl.formatMessage({
                            id: 'location.addressLabel',
                        })}
                    />
                    <ExternalLink
                        className={styles.address}
                        onClick={trackGetDirections}
                        href={mapsLink}
                    >
                        <address>{addressString}</address>
                    </ExternalLink>
                </div>
            )}
            {locationHours && (
                <div className={styles.iconRow}>
                    <Clock
                        aria-label={intl.formatMessage({
                            id: 'location.hoursLabel',
                        })}
                        className={styles.clock}
                    />
                    <div className={styles.hours}>
                        {mapLocationHours(locationHours)}
                    </div>
                </div>
            )}
            <CallToAction
                className={styles.cta}
                variant="contained"
                size="small"
                onClick={() => {
                    if (location.city && location.shortSlug) {
                        startBooking({
                            locationId: id,
                            citySlug: location.city.slug,
                            locationSlug: location.shortSlug,
                        });
                    } else {
                        startBooking();
                    }
                }}
            >
                Book Now
            </CallToAction>
        </div>
    );
}

export default ClinicCard;
